import React from 'react';

import { Button, Grid, TextField } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Stack } from '@liscio/ui';

import { CreateAccountPrefTypeOptions, CreateAccountSectionProps } from './sectionTypes';
import { LabeledField, SubsectionTitle } from '../CreateAccountFormFieldPrimitives';
import { CreateAccountFormValues } from '../createAccountValidationSchema';
import { emptyDependentRow } from '../entityFormDefaults';
import { InlineRichOptionSelect } from '../InlineRichOptionSelect';
import { useHomeData } from 'fetch-utils/users/user-hooks';


export interface CreateAccountDependentsFieldsProps
  extends CreateAccountSectionProps {
  prefTypeOptions?: CreateAccountPrefTypeOptions;
}

export const CreateAccountDependentsFields: React.FC<
  CreateAccountDependentsFieldsProps
> = ({ layout = 'responsive', prefTypeOptions: prefTypeOptionsProp }) => {
  const { data: homeData } = useHomeData();
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<CreateAccountFormValues>();

  const prefTypeOptions =
    prefTypeOptionsProp ?? homeData?.data?.pref_type_options;
  const relationshipOptions = prefTypeOptions?.dependent_relationship;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'dependents',
  });

  const nameBreakpoint = layout === 'single-column' ? 12 : 4;
  const dependentErrors = errors.dependents;

  return (
    <Stack spacing={2}>
      <SubsectionTitle>Dependents Details</SubsectionTitle>

      {fields.map((field, index) => {
        const rowErrors = Array.isArray(dependentErrors)
          ? dependentErrors[index]
          : undefined;

        return (
          <Stack key={field.id} spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SubsectionTitle sx={{ mb: 0 }}>
                {`${index + 1}. Dependent`}
              </SubsectionTitle>
              {fields.length > 1 && (
                <Button
                  type="button"
                  size="small"
                  color="inherit"
                  onClick={() => remove(index)}
                >
                  Remove
                </Button>
              )}
            </Stack>

            <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
              <Grid item xs={12} sm={nameBreakpoint}>
                <LabeledField label="First Name">
                  <TextField
                    {...register(`dependents.${index}.first_name`)}
                    fullWidth
                    error={Boolean(rowErrors?.first_name)}
                    helperText={rowErrors?.first_name?.message}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} sm={nameBreakpoint}>
                <LabeledField label="Middle Name">
                  <TextField
                    {...register(`dependents.${index}.middle_name`)}
                    fullWidth
                    error={Boolean(rowErrors?.middle_name)}
                    helperText={rowErrors?.middle_name?.message}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} sm={nameBreakpoint}>
                <LabeledField label="Last Name">
                  <TextField
                    {...register(`dependents.${index}.last_name`)}
                    fullWidth
                    error={Boolean(rowErrors?.last_name)}
                    helperText={rowErrors?.last_name?.message}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} sm={nameBreakpoint}>
                <LabeledField label="Taxpayer ID">
                  <TextField
                    {...register(`dependents.${index}.encryptedSsn`)}
                    fullWidth
                    type="password"
                    error={Boolean(rowErrors?.encryptedSsn)}
                    helperText={rowErrors?.encryptedSsn?.message}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} sm={nameBreakpoint}>
                <LabeledField label="Birthdate">
                  <TextField
                    {...register(`dependents.${index}.birthdate`)}
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(rowErrors?.birthdate)}
                    helperText={rowErrors?.birthdate?.message}
                  />
                </LabeledField>
              </Grid>

              {relationshipOptions && (
                <Grid item xs={12} sm={nameBreakpoint}>
                  <LabeledField label="Relationship">
                    <InlineRichOptionSelect
                      name={`dependents.${index}.dependant_relationship_type`}
                      control={control}
                      data={relationshipOptions}
                      error={rowErrors?.dependant_relationship_type}
                      placeholder="Select..."
                    />
                  </LabeledField>
                </Grid>
              )}
            </Grid>
          </Stack>
        );
      })}

      <Button
        type="button"
        variant="text"
        onClick={() => append({ ...emptyDependentRow })}
        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
      >
        + Add another dependent
      </Button>
    </Stack>
  );
};

export default CreateAccountDependentsFields;
